import React, { useState, useRef, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { UploadCloud, X, ChevronDown, Image } from "lucide-react";
import styles from "../../_css/ContenetManagement.module.css";
import CustomDropdown from "../common/CustomDropdown";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import { notifyError, notifySuccess } from "../../utils/helper";
import { useDispatch } from "react-redux";
import { uploadVideoTutorials, addTutorial, upload_thumbnail } from "../../requests.js";
import RichTextEditor from "../CMS/RichTextEditor.js";

const UploadReadOnlyVideoModal = ({ show, onHide, onSuccess }) => {
    const dispatch = useDispatch();
    const [uploadType, setUploadType] = useState("video");
    const [videoFile, setVideoFile] = useState(null);
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [readOnlyFiles, setReadOnlyFiles] = useState([]);
    const [formData, setFormData] = useState({
        title: "",
        shortSummary: "", // New field for short summary
        description: "",
        descriptionHtml: "",
        duration: "",
        url: ""
    });
    const [uploadProgress, setUploadProgress] = useState(0);
    const [thumbnailUploadProgress, setThumbnailUploadProgress] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [thumbnailUploading, setThumbnailUploading] = useState(false);
    const [loading, setLoading] = useState(false);

    const contentTypeOptions = [
        { value: "video", label: "Video Tutorial" },
        { value: "readonly", label: "Read Only Document" }
    ];

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleRichTextChange = (html) => {
        setFormData(prev => ({ ...prev, descriptionHtml: html }));
    };

    const handleVideoFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const validVideoTypes = ['video/mp4', 'video/mov', 'video/avi', 'video/mkv', 'video/webm'];
            if (!validVideoTypes.includes(file.type)) {
                notifyError("Please upload a valid video file (MP4, MOV, AVI, MKV, WEBM)");
                return;
            }
            const maxSize = 50 * 1024 * 1024;
            if (file.size > maxSize) {
                notifyError("File size must be less than 50MB");
                return;
            }
            setVideoFile(file);
            setFormData(prev => ({ ...prev, url: "" }));
        }
    };

    const handleThumbnailChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
            if (!validImageTypes.includes(file.type)) {
                notifyError("Please upload a valid image file (JPEG, PNG, GIF, WEBP)");
                return;
            }
            const maxSize = 5 * 1024 * 1024;
            if (file.size > maxSize) {
                notifyError("Thumbnail size must be less than 5MB");
                return;
            }
            setThumbnailFile(file);
        }
    };

    const handleReadOnlyFileChange = (e) => {
        const files = Array.from(e.target.files);
        setReadOnlyFiles(prevFiles => [...prevFiles, ...files]);
        setFormData(prev => ({ ...prev, url: "" }));
    };

    const removeVideoFile = () => setVideoFile(null);
    const removeThumbnail = () => setThumbnailFile(null);
    const removeReadOnlyFile = (index) => setReadOnlyFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    const triggerThumbnailInput = () => document.getElementById('upload-thumbnail').click();

    const validateForm = () => {
        if (!formData.title.trim()) {
            notifyError("Please enter a title");
            return false;
        }

        if (uploadType === "readonly") {
            if (!formData.shortSummary.trim()) {
                notifyError("Please enter a short summary");
                return false;
            }
            if (!formData.descriptionHtml.trim()) {
                notifyError("Please enter a description");
                return false;
            }
            if (!thumbnailFile) {
                notifyError("Please upload a thumbnail");
                return false;
            }
            // if (readOnlyFiles.length === 0 && !formData.url) {
            //     notifyError("Please upload documents or provide a URL");
            //     return false;
            // }
        } else {
            // Video type validation
            if (!formData.description.trim()) {
                notifyError("Please enter a description");
                return false;
            }
            if (!videoFile && !formData.url) {
                notifyError("Please upload a video file or provide a URL");
                return false;
            }
            if (!formData.duration.trim()) {
                notifyError("Please enter video duration");
                return false;
            }
        }
        return true;
    };

    const uploadThumbnail = async (file) => {
        try {
            setThumbnailUploading(true);
            setThumbnailUploadProgress(0);
            const thumbnailResponse = await upload_thumbnail(file);
            setThumbnailUploadProgress(100);
            return thumbnailResponse.blobUrl;
        } catch (error) {
            notifyError("Failed to upload thumbnail");
            throw error;
        } finally {
            setThumbnailUploading(false);
        }
    };

    const handleVideoUpload = async () => {
        try {
            let uploadPayload = {
                title: formData.title,
                description: formData.description,
                duration: formData.duration,
                thumbnail: "",
                filterType: "video",
                tags: ["Video Tutorial"],
                cta: "Watch Now",
                ctaLink: "",
                status: "published"
            };
            if (thumbnailFile) {
                const thumbnailUrl = await uploadThumbnail(thumbnailFile);
                uploadPayload.thumbnail = thumbnailUrl;
            }
            if (videoFile) {
                setUploading(true);
                const videoResponse = await uploadVideoTutorials(videoFile, (p) => setUploadProgress(p));
                setUploading(false);
                setUploadProgress(100);
                uploadPayload.ctaLink = videoResponse.blobUrl;
            } else if (formData.url) {
                uploadPayload.ctaLink = formData.url;
            }
            return await addTutorial(uploadPayload);
        } catch (error) {
            throw error;
        }
    };

    const handleReadOnlyUpload = async () => {
        try {
            const uploadPayload = {
                title: formData.title,
                shortSummary: formData.shortSummary, // Include short summary in payload
                description: formData.descriptionHtml || formData.description,
                duration: "",
                thumbnail: "",
                filterType: "document",
                tags: ["Document Tutorial"],
                cta: "Read Now",
                ctaLink: "",
                status: "published"
            };

            if (thumbnailFile) {
                const thumbnailUrl = await uploadThumbnail(thumbnailFile);
                uploadPayload.thumbnail = thumbnailUrl;
            }

            if (readOnlyFiles.length > 0) {
                // For read-only files, you might want to upload them similar to videos
                // For now, using object URL, but you might need actual file upload
                uploadPayload.ctaLink = URL.createObjectURL(readOnlyFiles[0]);
            } else if (formData.url) {
                uploadPayload.ctaLink = formData.url;
            }

            return await addTutorial(uploadPayload);
        } catch (error) {
            throw error;
        }
    };

    const handlePublish = async () => {
        // Properly check validation result
        if (!validateForm()) {
            return;
        }

        dispatch(startLoader());
        setLoading(true);
        try {
            const response =
                uploadType === "video"
                    ? await handleVideoUpload()
                    : await handleReadOnlyUpload();

            if (response?.success) {
                notifySuccess("Tutorial uploaded successfully!");
                resetForm();
                onHide();
                if (onSuccess) onSuccess();
            } else {
                notifyError("Failed to upload tutorial");
            }
        } catch (error) {
            notifyError(error.response?.data?.message || "Failed to upload tutorial");
        } finally {
            dispatch(stopLoader());
            setLoading(false);
            setUploading(false);
        }
    };

    const handleSaveAsDraft = async () => {
        if (!formData.title.trim()) {
            notifyError("Please enter a title to save as draft");
            return;
        }
        dispatch(startLoader());
        setLoading(true);
        try {
            const draftPayload = {
                title: formData.title,
                shortSummary: formData.shortSummary, // Include short summary in draft
                description: uploadType === "readonly" ? formData.descriptionHtml : formData.description,
                thumbnail: "",
                duration: formData.duration,
                filterType: uploadType === "video" ? "video" : "document",
                tags: ["Draft"],
                cta: "Publish",
                ctaLink: formData.url || "",
                status: "draft"
            };
            if (thumbnailFile) {
                const thumbnailUrl = await uploadThumbnail(thumbnailFile);
                draftPayload.thumbnail = thumbnailUrl;
            }
            await addTutorial(draftPayload);
            notifySuccess("Tutorial saved as draft successfully!");
            resetForm();
            onHide();
            if (onSuccess) onSuccess();
        } catch (error) {
            notifyError(error.response?.data?.message || "Failed to save as draft");
        } finally {
            dispatch(stopLoader());
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            title: "",
            shortSummary: "", // Reset short summary
            description: "",
            descriptionHtml: "",
            duration: "",
            url: ""
        });
        setVideoFile(null);
        setThumbnailFile(null);
        setReadOnlyFiles([]);
        setUploadType("video");
        setUploadProgress(0);
        setThumbnailUploadProgress(0);
        setUploading(false);
        setThumbnailUploading(false);
    };

    useEffect(() => {
        if (!show) resetForm();
    }, [show]);

    const isFormDisabled = loading || uploading || thumbnailUploading;

    return (
        <Modal
            show={show}
            onHide={onHide}
            centered
            dialogClassName="modal-38"
        >
            <Modal.Body className={isFormDisabled ? styles.tutorialModal__disabled : ""}>
                <div className={styles.tutorialModal__container}>
                    {/* Header */}
                    <div className={styles.tutorialModal__header}>
                        <X
                            onClick={onHide}
                            className={styles.tutorialModal__closeButton}
                            size={28}
                        />
                        <span className={styles.tutorialModal__title}>
                            Upload Tutorial Content
                        </span>
                    </div>

                    {/* Upload Type Selection */}
                    <div className={styles.tutorialModal__formSection}>
                        <CustomDropdown
                            value={uploadType}
                            options={contentTypeOptions}
                            onChange={setUploadType}
                            label="Content Type"
                        />
                    </div>

                    {/* Title */}
                    <div className={styles.tutorialModal__formSection}>
                        <div className={styles.tutorialModal__flex1}>
                            <label className={styles.tutorialModal__label}>Title *</label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={(e) => handleInputChange("title", e.target.value)}
                                placeholder={uploadType === "video" ? "Ted Talk" : "Document Title"}
                                className={styles.tutorialModal__input}
                                disabled={isFormDisabled}
                            />
                        </div>
                    </div>

                    {/* Short Summary - Only for Readonly */}
                    {uploadType === "readonly" &&
                        <div className={styles.tutorialModal__formSection}>
                            <div className={styles.tutorialModal__flex1}>
                                <label className={styles.tutorialModal__label}>Short Summary *</label>
                                <input
                                    type="text"
                                    value={formData.shortSummary}
                                    onChange={(e) => handleInputChange("shortSummary", e.target.value)}
                                    placeholder="Enter a brief summary of the document..."
                                    className={styles.tutorialModal__input}
                                    disabled={isFormDisabled}
                                />
                            </div>
                        </div>
                    }

                    {/* Thumbnail Upload Section */}
                    <div className={styles.tutorialModal__formSection}>
                        <label className={styles.tutorialModal__label}>
                            Upload Thumbnail {thumbnailUploading && "(Uploading...)"}
                        </label>
                        <div className={styles.tutorialModal__thumbnailContainer}>
                            {thumbnailFile ? (
                                <div className={styles.tutorialModal__thumbnailPreview}>
                                    <img
                                        src={URL.createObjectURL(thumbnailFile)}
                                        alt="Thumbnail preview"
                                        className={styles.tutorialModal__thumbnailImage}
                                    />
                                    <div className={styles.tutorialModal__thumbnailActions}>
                                        <span className={styles.tutorialModal__fileName}>{thumbnailFile.name}</span>
                                        <X
                                            size={16}
                                            onClick={removeThumbnail}
                                            className={styles.tutorialModal__cursorPointer}
                                            disabled={isFormDisabled}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div
                                    className={`${styles.tutorialModal__uploadArea} ${isFormDisabled ? styles.tutorialModal__uploadAreaDisabled : ''}`}
                                    onClick={isFormDisabled ? undefined : triggerThumbnailInput}
                                >
                                    <Image size={20} color="#000000" />
                                    <span className={styles.tutorialModal__uploadText}>Click to upload thumbnail</span>
                                </div>
                            )}
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleThumbnailChange}
                                style={{ display: 'none' }}
                                id="upload-thumbnail"
                                disabled={isFormDisabled}
                            />
                        </div>
                        <p className={styles.tutorialModal__helpText}>
                            Recommended: 16:9 aspect ratio, max 5MB (JPEG, PNG, GIF, WEBP)
                        </p>

                        {thumbnailUploading && (
                            <div className={styles.tutorialModal__progressContainer}>
                                <div
                                    className={styles.tutorialModal__progressBar}
                                    style={{ width: `${thumbnailUploadProgress}%` }}
                                ></div>
                            </div>
                        )}
                    </div>

                    {/* Description */}
                    {/*  For Readonly - Rich Text Description */}
                    {uploadType === "readonly" ? (
                        <>
                            <div className={styles.tutorialModal__formSection}>
                                <label className={styles.tutorialModal__label}>Description *</label>
                                <RichTextEditor
                                    value={formData.descriptionHtml}
                                    onChange={handleRichTextChange}
                                    label="Description"
                                />
                            </div>
                        </>
                    ) : (
                        //  Show normal video description input
                        <div className={styles.tutorialModal__formSection}>
                            <label className={styles.tutorialModal__label}>Description *</label>
                            <input
                                type="text"
                                value={formData.description}
                                onChange={(e) => handleInputChange("description", e.target.value)}
                                placeholder="Enter description"
                                className={styles.tutorialModal__input}
                                disabled={isFormDisabled}
                            />
                        </div>
                    )}

                    {/* Video Upload Section */}
                    {uploadType === "video" && (
                        <>
                            <div className={styles.tutorialModal__fileUploadSection}>
                                {/* Duration */}
                                <div className={styles.tutorialModal__flex1}>
                                    <label className={styles.tutorialModal__label}>Duration *</label>
                                    <input
                                        type="text"
                                        value={formData.duration}
                                        onChange={(e) => handleInputChange("duration", e.target.value)}
                                        placeholder="2min"
                                        className={styles.tutorialModal__input}
                                        disabled={isFormDisabled}
                                    />
                                </div>
                                {/* Upload Video */}
                                <div className={styles.tutorialModal__flex1}>
                                    <label className={styles.tutorialModal__label}>Upload Video</label>
                                    <div
                                        className={`${styles.tutorialModal__input} ${styles.tutorialModal__fileDisplay} ${isFormDisabled ? styles.tutorialModal__uploadAreaDisabled : ''}`}
                                        onClick={isFormDisabled ? undefined : () => document.getElementById('upload-video').click()}
                                    >
                                        <span>
                                            {videoFile ? videoFile.name : "Choose video file..."}
                                        </span>
                                        {videoFile && (
                                            <X
                                                size={16}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeVideoFile();
                                                }}
                                                className={styles.tutorialModal__cursorPointer}
                                            />
                                        )}
                                    </div>
                                    <input
                                        type="file"
                                        accept="video/*"
                                        onChange={handleVideoFileChange}
                                        className={styles.tutorialModal__hidden}
                                        id="upload-video"
                                        disabled={isFormDisabled}
                                    />

                                    {uploading && (
                                        <div className={styles.tutorialModal__progressContainer}>
                                            <div
                                                className={styles.tutorialModal__progressBar}
                                                style={{ width: `${uploadProgress}%` }}
                                            ></div>
                                        </div>
                                    )}

                                    {uploadProgress === 100 && !uploading && (
                                        <p className={styles.tutorialModal__successMessage}>✅ Video uploaded successfully</p>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {/* URL Upload Section */}
                    {uploadType === "video" && (<div className={styles.tutorialModal__formSection}>
                        <label className={styles.tutorialModal__label}>or Upload the URL</label>
                        <div className={styles.tutorialModal__urlContainer}>
                            <input
                                type="text"
                                value={formData.url}
                                onChange={(e) => handleInputChange("url", e.target.value)}
                                placeholder={uploadType === "video" ? "https://www.youtube.com/watch?v=..." : "https://www.figma.com/design/..."}
                                className={styles.tutorialModal__urlInput}
                                disabled={isFormDisabled || (uploadType === "video" ? !!videoFile : readOnlyFiles.length > 0)}
                            />
                            {/* <button
                                className={styles.tutorialModal__uploadButton}
                                onClick={() => {
                                    if (!formData.url) {
                                        notifyError("Please enter a URL");
                                        return;
                                    }
                                    notifySuccess("URL added successfully!");
                                }}
                                disabled={isFormDisabled || !formData.url.trim() || (uploadType === "video" ? !!videoFile : readOnlyFiles.length > 0)}
                            >
                                Upload
                            </button> */}
                        </div>
                    </div>)}

                    {/* Action Buttons */}
                    <div className={styles.tutorialModal__actionButtons}>
                        <button
                            className={styles.tutorialModal__draftButton}
                            onClick={handleSaveAsDraft}
                            disabled={isFormDisabled}
                        >
                            Save As Draft
                        </button>
                        <button
                            className={styles.tutorialModal__publishButton}
                            onClick={handlePublish}
                            disabled={isFormDisabled}
                        >
                            Publish
                        </button>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default UploadReadOnlyVideoModal;